import { FunctionComponent, useState, useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import styles from './SearchFiltersModal.module.css';

interface FilterOptions {
  // Location
  distance: string;
  
  // Cost
  priceRange: {
    min: number;
    max: number;
  };
  
  // Provider specific
  rating: number;
  availability: string;
  specialties: string[];
  insuranceAccepted: boolean;
  
  // Procedure specific
  facilityType: string[];
  
  // Medication specific
  genericOnly: boolean;
  brandOnly: boolean;
  
  // General
  resultTypes: string[];
}

interface SearchFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  currentFilters: FilterOptions;
  availableTypes: string[];
}

const SearchFiltersModal: FunctionComponent<SearchFiltersModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters,
  availableTypes
}) => {
  const [filters, setFilters] = useState<FilterOptions>(currentFilters);
  const [activeSection, setActiveSection] = useState<'general' | 'location' | 'cost' | 'quality' | 'type'>('general');

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    const defaultFilters: FilterOptions = {
      distance: '10',
      priceRange: { min: 0, max: 10000 },
      rating: 0,
      availability: 'any',
      specialties: [],
      insuranceAccepted: false,
      facilityType: [],
      genericOnly: false,
      brandOnly: false,
      resultTypes: []
    };
    setFilters(defaultFilters);
  };

  const updateFilter = (key: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      contentRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filters-title"
        tabIndex={-1}
        ref={contentRef}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="filters-title" className={styles.title}>Filter Results</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close filters">
            <CloseIcon />
          </button>
        </div>

        {/* Filter Sections */}
        <div className={styles.filterContainer}>
          {/* Section Tabs */}
          <div className={styles.sectionTabs}>
            <button
              className={`${styles.sectionTab} ${activeSection === 'general' ? styles.active : ''}`}
              onClick={() => setActiveSection('general')}
            >
              General
            </button>
            <button
              className={`${styles.sectionTab} ${activeSection === 'location' ? styles.active : ''}`}
              onClick={() => setActiveSection('location')}
            >
              Location
            </button>
            <button
              className={`${styles.sectionTab} ${activeSection === 'cost' ? styles.active : ''}`}
              onClick={() => setActiveSection('cost')}
            >
              Cost
            </button>
            <button
              className={`${styles.sectionTab} ${activeSection === 'quality' ? styles.active : ''}`}
              onClick={() => setActiveSection('quality')}
            >
              Quality
            </button>
            {availableTypes.includes('medication') && (
              <button
                className={`${styles.sectionTab} ${activeSection === 'type' ? styles.active : ''}`}
                onClick={() => setActiveSection('type')}
              >
                Type-Specific
              </button>
            )}
          </div>

          {/* Filter Content */}
          <div className={styles.filterContent}>
            {/* General Filters */}
            {activeSection === 'general' && (
              <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>Result Types</h3>
                <div className={styles.checkboxGroup}>
                  {['provider', 'procedure', 'medication', 'article', 'condition'].map(type => (
                    <label key={type} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={filters.resultTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateFilter('resultTypes', [...filters.resultTypes, type]);
                          } else {
                            updateFilter('resultTypes', filters.resultTypes.filter(t => t !== type));
                          }
                        }}
                      />
                      <span>{type.charAt(0).toUpperCase() + type.slice(1)}s</span>
                    </label>
                  ))}
                </div>

                <h3 className={styles.sectionTitle}>Insurance</h3>
                <label className={styles.switchLabel}>
                  <span>In-Network Only</span>
                  <div className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={filters.insuranceAccepted}
                      onChange={(e) => updateFilter('insuranceAccepted', e.target.checked)}
                    />
                    <span className={styles.slider}></span>
                  </div>
                </label>
              </div>
            )}

            {/* Location Filters */}
            {activeSection === 'location' && (
              <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>
                  <LocationOnIcon className={styles.sectionIcon} />
                  Distance
                </h3>
                <div className={styles.distanceOptions}>
                  {['1', '5', '10', '25', '50'].map(distance => (
                    <button
                      key={distance}
                      className={`${styles.distanceOption} ${filters.distance === distance ? styles.active : ''}`}
                      onClick={() => updateFilter('distance', distance)}
                    >
                      {distance} mi
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cost Filters */}
            {activeSection === 'cost' && (
              <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>
                  <AttachMoneyIcon className={styles.sectionIcon} />
                  Price Range
                </h3>
                <div className={styles.priceRange}>
                  <div className={styles.priceInput}>
                    <label>Min</label>
                    <input
                      type="number"
                      value={filters.priceRange.min}
                      onChange={(e) => updateFilter('priceRange', {
                        ...filters.priceRange,
                        min: Number(e.target.value)
                      })}
                      placeholder="0"
                    />
                  </div>
                  <span className={styles.priceSeparator}>–</span>
                  <div className={styles.priceInput}>
                    <label>Max</label>
                    <input
                      type="number"
                      value={filters.priceRange.max}
                      onChange={(e) => updateFilter('priceRange', {
                        ...filters.priceRange,
                        max: Number(e.target.value)
                      })}
                      placeholder="10000"
                    />
                  </div>
                </div>
                <div className={styles.pricePresets}>
                  <button
                    className={styles.presetButton}
                    onClick={() => updateFilter('priceRange', { min: 0, max: 50 })}
                  >
                    Under $50
                  </button>
                  <button
                    className={styles.presetButton}
                    onClick={() => updateFilter('priceRange', { min: 0, max: 100 })}
                  >
                    Under $100
                  </button>
                  <button
                    className={styles.presetButton}
                    onClick={() => updateFilter('priceRange', { min: 0, max: 500 })}
                  >
                    Under $500
                  </button>
                </div>
              </div>
            )}

            {/* Quality Filters */}
            {activeSection === 'quality' && (
              <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>
                  <StarIcon className={styles.sectionIcon} />
                  Minimum Rating
                </h3>
                <div className={styles.ratingOptions}>
                  {[0, 3, 3.5, 4, 4.5].map(rating => (
                    <button
                      key={rating}
                      className={`${styles.ratingOption} ${filters.rating === rating ? styles.active : ''}`}
                      onClick={() => updateFilter('rating', rating)}
                    >
                      {rating === 0 ? 'Any' : (
                        <>
                          <StarIcon className={styles.starIcon} />
                          {rating}+
                        </>
                      )}
                    </button>
                  ))}
                </div>

                <h3 className={styles.sectionTitle}>
                  <AccessTimeIcon className={styles.sectionIcon} />
                  Availability
                </h3>
                <div className={styles.availabilityOptions}>
                  {[
                    { value: 'any', label: 'Any time' },
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'This week' },
                    { value: 'month', label: 'This month' }
                  ].map(option => (
                    <button
                      key={option.value}
                      className={`${styles.availabilityOption} ${filters.availability === option.value ? styles.active : ''}`}
                      onClick={() => updateFilter('availability', option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Type-Specific Filters */}
            {activeSection === 'type' && availableTypes.includes('medication') && (
              <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>Medication Type</h3>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="medicationType"
                      checked={!filters.genericOnly && !filters.brandOnly}
                      onChange={() => {
                        updateFilter('genericOnly', false);
                        updateFilter('brandOnly', false);
                      }}
                    />
                    <span>All medications</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="medicationType"
                      checked={filters.genericOnly}
                      onChange={() => {
                        updateFilter('genericOnly', true);
                        updateFilter('brandOnly', false);
                      }}
                    />
                    <span>Generic only</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="medicationType"
                      checked={filters.brandOnly}
                      onChange={() => {
                        updateFilter('genericOnly', false);
                        updateFilter('brandOnly', true);
                      }}
                    />
                    <span>Brand name only</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.resetButton} onClick={handleReset}>
            Reset All
          </button>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button className={styles.applyButton} onClick={handleApply}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFiltersModal;
